import { warfishClassicsMapStaleTime } from '../components/map/warfish/presentation'
import { isQaBuild } from '../lib/qaBuild'
/**
 * useMaps — React-Query hooks for the registered map library.
 *
 *   useMaps()     → MapSummary list (counts and picker presentation metadata)
 *   useMap(id)    → full MapDef (territories, regions, viewBox, adjacencies)
 *
 * Used by the Map Details screen, which needs the full MapDef pre-game
 * (lobby — before any GameState exists). Cached forever once loaded
 * because the registered maps don't change at runtime.
 *
 * Test-only maps (e.g. `test-quad` for the Maestro game-play flow) are
 * server-side filtered out of GET /api/v1/maps by default. To opt the
 * picker into them, run against a non-production API — no flag needed
 * (Maestro test build does this) or — for local one-off dev — flip the
 * — the server decides, so there is nothing to override client-side
 * before the bundle loads. Production builds never set either, so the
 * test-quad map is invisible to real users.
 *
 * COLD-START OPTIMISATION (Tier 1 #1)
 * ------------------------------------
 * Both hooks use bundled JSON as placeholder data (copied by
 * `pnpm --filter @warcarp/mobile bundle-maps`) before the network request
 * completes. The placeholder is not written into the React Query cache, so
 * the API remains authoritative and replaces it when the request resolves.
 * Full JSON is loaded lazily per map; the summary list stays lightweight.
 * `prefetchMap` warms a likely next map in the shared cache without making
 * the current screen wait for geometry.
 */

import { useQuery } from '@tanstack/react-query'
import { apiGet } from '../lib/api'
import { queryClient } from '../lib/queryClient'
import { BUNDLED_MAP_LIST, getBundledMap } from '../lib/bundledMaps'
import type { MapDef, MapSummary } from '@warcarp/api-types'
export type { MapSummary } from '@warcarp/api-types'

const QK = {
  /**
   * One list key. There is no test-vs-production variant any more: the SERVER
   * decides whether its test maps are in the list (see apps/api/src/routes/
   * maps.ts), so the client just asks for "the maps" and renders what it gets.
   */
  list: () => ['maps', 'list'] as const,
  detail: (id: string) => ['maps', id] as const,
}

/**
 * Bundled maps as PLACEHOLDER data — deliberately not written into the query
 * cache.
 *
 * This used to `setQueryData` the bundled MapDef into `['maps', id]` at module
 * init. Combined with `staleTime: Infinity` that made the seeded entry
 * permanently fresh, so React Query never fetched and **the network copy could
 * never replace it**. A map edited in the editor and applied to the engine kept
 * rendering the old geometry, colours and bonuses until a new bundle was built —
 * which reads as "my overrides didn't take" and sends you looking at the
 * pipeline instead of at the cache. (The old doc comment claimed it passed
 * `updatedAt: 0` to avoid exactly this. It didn't — and it wouldn't have helped:
 * `staleTime: Infinity` ignores `updatedAt`.)
 *
 * `placeholderData` gives the same instant first paint without occupying the
 * cache entry, so the query still runs exactly once per session (staleTime is
 * still Infinity) and the server's copy wins. Cost: one request per map per
 * session, which is what the decoupling in GameState was already paying for.
 *
 * Returns undefined for maps that aren't bundled (test-quad) — those just show
 * a spinner until the fetch lands, as before.
 */
function bundledMapPlaceholder(id: string | undefined): MapDef | undefined {
  if (!id) return undefined
  return getBundledMap(id)
}

export function useMaps(options?: { enabled?: boolean }) {
  const path = '/api/v1/maps'
  return useQuery({
    queryKey: QK.list(),
    queryFn: () => apiGet<MapSummary[]>(path),
    enabled: options?.enabled !== false,
    // Paint the bundled list instantly, then let the fetch replace it. The
    // bundled list has no test maps (test-quad isn't bundled), so on a dev
    // server the picker fills in a moment later.
    placeholderData: BUNDLED_MAP_LIST as MapSummary[],
    staleTime: Infinity, // fetched once per session; the registry is static per deploy
    gcTime: Infinity,    // never evict — static content, no reason to re-fetch after unmount
  })
}

/**
 * Warm one map's network/query cache without making a screen wait for it.
 *
 * The bundled copy is deliberately only a placeholder: calling this loads
 * the anticipated asset for an instant offline/cold-start fallback, while
 * React Query fetches the authoritative server copy into the shared cache.
 * `prefetchQuery` also coalesces with a mounted `useMap` query, so callers can
 * safely invoke this when a map becomes likely to be needed.
 */
export function prefetchMap(mapId: string | undefined): void {
  if (!mapId) return
  getBundledMap(mapId)
  void queryClient.prefetchQuery({
    queryKey: QK.detail(mapId),
    queryFn: () => apiGet<MapDef>(`/api/v1/maps/${mapId}`),
    staleTime: warfishClassicsMapStaleTime(getBundledMap(mapId), isQaBuild()),
    gcTime: Infinity,
  })
}

export function useMap(id: string | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: QK.detail(id ?? ''),
    queryFn: () => apiGet<MapDef>(`/api/v1/maps/${id}`),
    enabled: !!id && options?.enabled !== false,
    placeholderData: bundledMapPlaceholder(id),
    staleTime: warfishClassicsMapStaleTime(getBundledMap(id), isQaBuild()),
    gcTime: Infinity,    // never evict — map defs are static
  })
}
